// @ts-nocheck

import type { EChartsType } from "echarts/core"
import type { CallbackDataParams } from "echarts/types/dist/shared"
import { useEffect, useRef } from "react"
import useComponentSize from "@rehooks/component-size"
import { format } from "date-fns"

import { formatNumber, toFormattedValueN } from "@/domains/library"
import useEcharts from "@/hooks/useEcharts"

// Lukes colour guide from https://www.figma.com/design/OkoolaUqJHmF7WdvIR3Mwm/Landing-Page-Design?node-id=118-20310&node-type=frame&t=a2s85T68r1BEJxrV-0
const colors = [
  "#1A56CD",
  "#5A7CFF",
  "#9CA8FF",
  "#38C4B8",
  "#DB14A9",
  "#FACA87",
  "#F0626E",
  "#09D993",
  "#DB1D2C",
  "#CF7F0E",
]

export type TooltipFormatterType = CallbackDataParams & { axisValue: number; marker: string }

export type VolumeDataItem = {
  amount: string
  source: string
  protocol: string
  timestamp: string
}

const VolumeChart = ({ data }: { data: Array<VolumeDataItem> }) => {
  const chart = useRef<HTMLDivElement | null>(null)
  const echarts = useEcharts()
  const size = useComponentSize(chart)
  let chartInstance: EChartsType | null = null

  const left = size.width <= 640 ? "40px" : "48px"
  const bottom = size.width <= 640 ? "72px" : "80px"
  const fontSize = size.width <= 640 ? "8px" : "14px"

  const grid = {
    left,
    right: "16px",
    top: "40px",
    bottom,
  }

  const processedData = data.reduce(
    (acc, item) => {
      const existingTimestamp = acc.find((d) => d.timestamp === item.timestamp)
      if (existingTimestamp) {
        existingTimestamp[item.source] = Number(item.amount)
      } else {
        acc.push({
          timestamp: item.timestamp,
          [item.source]: Number(item.amount),
        })
      }
      return acc
    },
    [] as Array<{ timestamp: string; [key: string]: number | string }>,
  )

  const xAxisData = processedData.map((item) => item.timestamp)
  const sources = Array.from(new Set(data.map((item) => item.source)))

  const series = sources.map((source, index) => {
    let color = colors[index % colors.length]
    switch (source) {
      case "Equalizer Exchange":
        color = colors[1]
        break
      case "Scale":
        color = colors[1]
        break
      case "E3":
        color = colors[2]
        break
      case "Thick":
        color = colors[0]
        break
    }

    return {
      name: source,
      type: "bar",
      stack: "total",
      barWidth: "60%",
      itemStyle: {
        color: color,
      },
      data: processedData.map((item) => item[source] || 0),
    }
  })

  const option = {
    legend: {
      selectedMode: true,
      textStyle: {
        color: "#FFFFFF", // Set legend text to white
      },
    },
    grid,
    yAxis: {
      type: "value",
      axisLabel: {
        fontSize,
        formatter: (value: number) => {
          return formatNumber(value)
        },
        color: "#FFFFFF",
      },
      splitLine: {
        show: true,
        lineStyle: { color: "#272C4F", width: 1, type: "solid" },
      },
    },
    xAxis: {
      type: "category",
      data: xAxisData,
      axisLabel: {
        interval: 1,
        rotate: 0,
        fontSize: 16,
        margin: 16,
        formatter: function (value: number, index: number) {
          const n = 3
          return index % n === 0 ? format(new Date(value * 1000), "dd/MM/yyyy") : ""
        },
      },
    },
    series: series,
    dataZoom: [
      {
        show: true,
        start: 60,
        end: 100,
        height: 20,
        bottom: 6,
      },
      {
        type: "inside",
        start: 20,
        end: 100,
      },
      {
        show: false,
        yAxisIndex: 0,
        filterMode: "empty",
        width: 60,
        height: "80%",
        showDataShadow: false,
        left: "93%",
      },
    ],
    tooltip: {
      trigger: "axis",
      responsive: false,
      backgroundColor: "#111829E0",
      borderColor: "#1E2B45",
      borderWidth: "0.8",
      textStyle: {
        color: "#8FA2C7",
      },
      axisPointer: {
        type: "shadow",
      },
      formatter: function (params: TooltipFormatterType[]) {
        if (!Array.isArray(params)) return
        if (!params[0]) return
        const total = params.reduce((acc, item) => acc + Number(item.value), 0)

        let result = `<div>${format(new Date(params[0].axisValue * 1000), "dd/MM/yyyy")}</div>`
        let cumulativeTotal = 0
        params.forEach(function (item) {
          const value = Number(item.value)
          cumulativeTotal += value
          const percentage = ((value / total) * 100).toFixed(2)
          result += `<div>${item.marker.replace(";border-radius:10px", "")} ${item.seriesName}: <span style="color: white;">$${toFormattedValueN(value, 0)} (${percentage}%)</span></div>`
        })
        result += `<div style="margin-top: 8px; border-top: 1px solid #1E2B45; padding-top: 8px;">Total: <span style="color: white;">$${toFormattedValueN(cumulativeTotal, 0)}</span></div>`
        return result
      },
    },
  }

  function renderChart() {
    if (!chart.current) return
    const renderInstance = echarts.getInstanceByDom(chart.current)

    if (renderInstance) {
      chartInstance = renderInstance
    } else {
      chartInstance = echarts.init(chart.current, "equalizer_2")
    }
    chartInstance.setOption(option)
  }

  useEffect(() => {
    renderChart()
    if (chartInstance != null) {
      chartInstance.resize({
        height: 480,
      })
    }
  }, [chartInstance, size])

  return <div ref={chart} className="w-full" />
}

export default VolumeChart
